'use client';

import React from 'react';
import { GameImagePlaceholder } from '@/components/ThemedPlaceholders';

type Props = {
  src?: string | null;
  srcCandidates?: string[];
  alt: string;
  title?: string;
  imageClassName: string;
  placeholderTitle?: string;
  placeholderContainerClassName?: string;
};

export function SafeGameImage({
  src,
  srcCandidates = [],
  alt,
  title,
  imageClassName,
  placeholderTitle,
  placeholderContainerClassName,
}: Props) {
  const normalizedSrc = String(src ?? '').trim();
  const candidates = React.useMemo(
    () =>
      Array.from(
        new Set(
          [normalizedSrc, ...srcCandidates]
            .map((value) => String(value ?? '').trim())
            .filter(Boolean),
        ),
      ),
    [normalizedSrc, srcCandidates],
  );
  const candidateKey = candidates.join('|');
  const [candidateIndex, setCandidateIndex] = React.useState(0);

  React.useEffect(() => {
    setCandidateIndex(0);
  }, [candidateKey]);

  const activeSrc = candidates[candidateIndex] ?? '';

  if (!activeSrc) {
    if (placeholderContainerClassName) {
      return (
        <div className={placeholderContainerClassName}>
          <GameImagePlaceholder title={placeholderTitle} />
        </div>
      );
    }
    return <GameImagePlaceholder title={placeholderTitle} />;
  }

  return (
    // eslint-disable-next-line @next/next/no-img-element
    <img
      src={activeSrc}
      alt={alt}
      title={title}
      width={320}
      height={268}
      loading="lazy"
      decoding="async"
      className={imageClassName}
      onError={() => setCandidateIndex((current) => current + 1)}
    />
  );
}
